import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useAppDispatch, useAppSelector } from '../../../State/Store';
import { fetchSellerProducts, updateProduct, deleteProduct } from '../../../State/seller/sellerProductSlice';
import { Product } from '../../../types/productTypes';
import { uploadToLocalhost } from '../../../config/uploadToLocalhost';
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Box,
  Stack,
  Typography,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Close, AddPhotoAlternate } from '@mui/icons-material';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export default function ProductTable() {
  const dispatch = useAppDispatch();
  const { sellerProduct } = useAppSelector((store) => store);

  // Edit Dialog State
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [formData, setFormData] = React.useState({
    title: '',
    description: '',
    mrpPrice: 0,
    sellingPrice: 0,
    color: '',
    images: [] as string[],
  });

  // Delete Confirmation Dialog State
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [productToDelete, setProductToDelete] = React.useState<Product | null>(null);

  React.useEffect(() => {
    dispatch(fetchSellerProducts(localStorage.getItem('jwt')));
  }, [dispatch]);

  // Edit Handlers
  const handleOpenEdit = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      mrpPrice: product.mrpPrice || 0,
      sellingPrice: product.sellingPrice || 0,
      color: product.color || '',
      images: product.images ? [...product.images] : [],
    });
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
    setSelectedProduct(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'mrpPrice' || name === 'sellingPrice' ? Number(value) : value,
    }));
  };

  // Image Upload Handler
  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const imageUrl = await uploadToLocalhost(file);
      if (imageUrl) {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, imageUrl],
        }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  // Image Remove Handler
  const handleRemoveImage = (indexToRemove: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const handleSaveProduct = () => {
    if (!selectedProduct) return;

    const updatedData = {
      productId: selectedProduct.id,
      product: {
        ...selectedProduct,
        ...formData,
      },
      jwt: localStorage.getItem('jwt') || '',
    };

    dispatch(updateProduct(updatedData as any));
    handleCloseEdit();
  };

  // Delete Handlers
  const handleOpenDelete = (product: Product) => {
    setProductToDelete(product);
    setOpenDeleteModal(true);
  };

  const handleCloseDelete = () => {
    setOpenDeleteModal(false);
    setProductToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!productToDelete || productToDelete.id === undefined) return;

    dispatch(
      deleteProduct({
        productId: productToDelete.id,
        jwt: localStorage.getItem('jwt') || '',
      })
    );
    handleCloseDelete();
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Images</StyledTableCell>
              <StyledTableCell align="right">Description</StyledTableCell>
              <StyledTableCell align="right">Title</StyledTableCell>
              <StyledTableCell align="right">MRP</StyledTableCell>
              <StyledTableCell align="right">Selling Price</StyledTableCell>
              <StyledTableCell align="right">Color</StyledTableCell>
              <StyledTableCell align="right">Update Stock</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sellerProduct.products.map((item: Product) => (
              <StyledTableRow key={item.id}>
                <StyledTableCell component="th" scope="row">
                  <div className="flex gap-1 flex-wrap">
                    {item.images?.map((image, idx) => (
                      <img key={idx} className="w-20 rounded-md object-cover h-20" alt="" src={image} />
                    ))}
                  </div>
                </StyledTableCell>
                <StyledTableCell align="right">{item.description}</StyledTableCell>
                <StyledTableCell align="right">{item.title}</StyledTableCell>
                <StyledTableCell align="right">{item.mrpPrice?.toLocaleString()}</StyledTableCell>
                <StyledTableCell align="right">{item.sellingPrice?.toLocaleString()}</StyledTableCell>
                <StyledTableCell align="right">{item.color}</StyledTableCell>
                <StyledTableCell align="right">
                  <Button size="small">in_stock</Button>
                </StyledTableCell>
                <StyledTableCell align="center">
                  <Box className="flex justify-center gap-1">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleOpenEdit(item)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleOpenDelete(item)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Product Dialog Modal */}
      <Dialog open={openEditModal} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Product Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
            />
            <Box className="flex gap-4">
              <TextField
                fullWidth
                type="number"
                label="MRP Price"
                name="mrpPrice"
                value={formData.mrpPrice}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                type="number"
                label="Selling Price"
                name="sellingPrice"
                value={formData.sellingPrice}
                onChange={handleInputChange}
              />
            </Box>
            <TextField
              fullWidth
              label="Color"
              name="color"
              value={formData.color}
              onChange={handleInputChange}
            />

            {/* Product Images Management Section */}
            <Box className="space-y-2 pt-2 border-t border-slate-200">
              <Typography variant="subtitle2" className="font-semibold text-slate-700">
                Product Images
              </Typography>

              <div className="flex flex-wrap gap-3 items-center">
                {/* File Upload Button */}
                <input
                  type="file"
                  accept="image/*"
                  id="editFileInput"
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
                <label className="relative" htmlFor="editFileInput">
                  <span className="w-20 h-20 cursor-pointer flex items-center justify-center p-3 border rounded-md border-gray-400 hover:border-gray-600 transition-colors">
                    <AddPhotoAlternate className="text-gray-700" />
                  </span>
                  {uploadingImage && (
                    <div className="absolute inset-0 w-20 h-20 flex justify-center items-center bg-white/60 rounded-md">
                      <CircularProgress size={24} />
                    </div>
                  )}
                </label>

                {/* Uploaded Images Preview Grid */}
                {formData.images.map((imgUrl, index) => (
                  <div key={index} className="relative group w-20 h-20 border rounded-md overflow-hidden bg-slate-50">
                    <img
                      src={imgUrl}
                      alt={`Product Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        backgroundColor: 'rgba(239, 68, 68, 0.9)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(220, 38, 38, 1)' },
                        width: 22,
                        height: 22,
                      }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </div>
                ))}
              </div>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveProduct} variant="contained" color="primary" disabled={uploadingImage}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog Modal */}
      <Dialog open={openDeleteModal} onClose={handleCloseDelete}>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{productToDelete?.title}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}